/**
 * Locale-aware formatting from the tenant's locale, currency and time zone (architecture §13.12).
 * Units follow CLAUDE.md: speed in pages/min, price as $ per page first, per 1,000 second.
 */
export interface TenantLocale {
  /** BCP 47 tag, e.g. 'en-US'. */
  locale: string;
  /** ISO 4217, e.g. 'USD'. */
  currency: string;
  /** IANA zone, e.g. 'America/New_York'; every timestamp is shown in it. */
  timeZone: string;
}

export const defaultLocale: TenantLocale = {
  locale: 'en-US',
  currency: 'USD',
  timeZone: 'UTC',
};

export interface Formatter {
  readonly locale: TenantLocale;
  int(n: number): string;
  decimal(n: number, digits?: number): string;
  money(n: number): string;
  /** Whole currency units for a headline: "$1,240", never "$1,240.00". */
  moneyWhole(n: number): string;
  /** Price per page, then per 1,000: "$0.012 per page ($12.00 per 1,000)". */
  perPage(pricePerPage: number): string;
  /** A percent 0..100. */
  percent(value: number, digits?: number): string;
  date(iso: string): string;
  time(iso: string): string;
  dateTime(iso: string): string;
  /** "4 min ago", "3 h ago", "2 d ago", or "never" for null. */
  ago(iso: string | null, now?: number): string;
  duration(seconds: number): string;
  storage(gb: number): string;
  /** "42 pages/min". */
  pagesPerMinute(n: number): string;
  list(items: string[]): string;
}

const relativeUnits: [Intl.RelativeTimeFormatUnit, number][] = [
  ['minute', 60],
  ['hour', 60],
  ['day', 24],
];

function agoFrom(rtf: Intl.RelativeTimeFormat, iso: string | null, now: number): string {
  if (!iso) return 'never';
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (seconds < 60) return 'just now';
  let value = seconds;
  let unit: Intl.RelativeTimeFormatUnit = 'second';
  for (const [u, factor] of relativeUnits) {
    if (value < factor) break;
    value = Math.round(value / factor);
    unit = u;
  }
  return rtf.format(-value, unit);
}

function durationOf(seconds: number, decimal: (n: number, d: number) => string): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172_800) return `${decimal(seconds / 3600, 1)} h`;
  return `${decimal(seconds / 86_400, 1)} d`;
}

/** The number shapes: whole, money, whole money for headlines, and the small money of a per-page price. */
function numberFormats(tag: string, currency: string) {
  return {
    int: new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }),
    money: new Intl.NumberFormat(tag, { style: 'currency', currency }),
    moneyWhole: new Intl.NumberFormat(tag, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }),
    smallMoney: new Intl.NumberFormat(tag, {
      style: 'currency',
      currency,
      minimumFractionDigits: 3,
      maximumFractionDigits: 4,
    }),
  };
}

export function createFormatter(locale: TenantLocale = defaultLocale): Formatter {
  const { locale: tag, currency, timeZone } = locale;
  const { int, money, moneyWhole, smallMoney } = numberFormats(tag, currency);
  const date = new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeZone });
  const time = new Intl.DateTimeFormat(tag, { timeStyle: 'short', timeZone });
  const dateTime = new Intl.DateTimeFormat(tag, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone,
  });
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: 'always', style: 'short' });
  const listFmt = new Intl.ListFormat(tag, { style: 'long', type: 'conjunction' });
  const decimal = (n: number, digits = 1): string =>
    new Intl.NumberFormat(tag, { maximumFractionDigits: digits }).format(n);
  return {
    locale,
    int: (n) => int.format(n),
    decimal,
    money: (n) => money.format(n),
    moneyWhole: (n) => moneyWhole.format(n),
    perPage: (p) => `${smallMoney.format(p)} per page (${money.format(p * 1000)} per 1,000)`,
    percent: (v, digits = 1) => `${decimal(v, digits)}%`,
    date: (iso) => date.format(new Date(iso)),
    time: (iso) => time.format(new Date(iso)),
    dateTime: (iso) => dateTime.format(new Date(iso)),
    ago: (iso, now = Date.now()) => agoFrom(rtf, iso, now),
    duration: (s) => durationOf(s, decimal),
    storage: (gb) => (gb >= 1000 ? `${decimal(gb / 1000, 2)} TB` : `${decimal(gb, 1)} GB`),
    pagesPerMinute: (n) => `${int.format(n)} pages/min`,
    list: (items) => listFmt.format(items),
  };
}
